'use strict'

import { axiosPost } from '../../axios/axios.js'
import { getReportProperties } from './Report.js'

export default class ReportManager {
  constructor (report) {
    this.report = report

    const reportProperties = this.getReportProperties(report)
    this.savedReportPropertiesString = JSON.stringify(reportProperties)
    this.savedReportProperties = JSON.parse(this.savedReportPropertiesString)

    this.validator = null // TODO report validation
  }

  // Общая объединенная логика =======================================

  validate (target) {
    if (!this.validator) {
      return true // TODO ?
    }
    const valid = this.validator.validate('save')
    if (!valid) {
      target && target.$q.notify({
        type: 'negative',
        message: 'Проверьте корректность данных отчёта',
        progress: true,
        timeout: 8000
      })
    }
    return valid
  }

  hasChanges () {
    if (!this.report.actionsAccess.hasAccessSaveChanges) {
      return false
    }
    const changesInfo = this.getChangesInfo()
    return changesInfo.hasChanges()
  }

  // private
  getChangesInfo () {
    return {
      imgFiles: this.hasFilesToUpload('guiImgFiles'),
      docFiles: this.hasFilesToUpload('guiDocFiles'),
      properties: this.hasReportPropertiesChanges(),
      // materials: this.hasMaterialsChanges() // TODO delete

      hasChanges: function () {
        return this.imgFiles || this.docFiles || this.properties // || this.materials
      }
    }
  }

  async saveChanges (config) {
    const { additionalProperties, ignoreValidation, force, target } = config || {}

    if (!ignoreValidation && !this.validate(target)) {
      throw new ReportError('Отчёт не прошёл валидацию', false)
    }

    const changesInfo = this.getChangesInfo()

    target && target.$q.loading.show({
      message: 'Сохранение отчёта',
      messageColor: 'blue-11',
      spinnerColor: 'blue-11'
    })

    let updateRequired = false
    let changed = false
    let hasErrors = false

    if (changesInfo.imgFiles) {
      try {
        const uploadResult = await this.uploadFiles('guiImgFiles', target)
        if (!updateRequired) {
          updateRequired = uploadResult.results.some(result => {
            return !!(result.value && result.value.data && result.value.data.relatedDataChanged)
          })
        }
        changed = true
      } catch (error) {
        hasErrors = true
      }
    }

    if (changesInfo.docFiles) {
      try {
        const uploadResult = await this.uploadFiles('guiDocFiles', target)
        if (!updateRequired) {
          updateRequired = uploadResult.results.some(result => {
            return !!(result.value && result.value.data && result.value.data.relatedDataChanged)
          })
        }
        changed = true
      } catch (error) {
        hasErrors = true
      }
    }

    // Для переходов в PR2 и PR9 теперь обязательно вносить изменения
    // if (changesInfo.properties) { // TODO проверить корректность переходов с проверкой изменений
    const stateId = this.report.stateId
    const bpStateId = this.taskStatus
    const notChangeState = ((stateId === 'PR2' || stateId === 'PR3') && bpStateId === 'BS7') ||
          (stateId == 'PR3' && !(bpStateId == 'BS4' || bpStateId == 'BS5' || bpStateId == 'BS6' || bpStateId == 'BS7' || bpStateId == 'BS14' || bpStateId == 'BS15')) ||
          stateId === 'PR4' || stateId === 'PR10' || stateId === 'PR7'
    try {
      changed = await this.saveReportProperties(notChangeState, additionalProperties, force, target)
      if (!updateRequired) {
        updateRequired = changed
      }
    } catch (error) {
      console.error(error)
      hasErrors = true
    }
    // }

    if (hasErrors) {
      throw new ReportError('Ошибка сохранения отчёта', changed)
    }
    return updateRequired // save result
  }

  // Основные данные (общий комментарий, замечания, работы, контракт, автовышки...) =======================================

  getReportProperties (report, savedReportProperties, includeOnlyChanges, includeMaterials) {
    return getReportProperties(report, savedReportProperties, includeOnlyChanges, includeMaterials)
  }

  hasReportPropertiesChanges () {
    const reportProperties = this.getReportProperties(this.report, this.savedReportProperties, true)
    return Object.keys(reportProperties).length !== 0
    // return this.savedReportPropertiesString !== JSON.stringify(this.getReportProperties(this.report))
  }

  saveReportProperties (checking, additionalProperties, force, target) {
    const report = this.report
    const includeOnlyChanges = true // TODO report.actionsAccess.reinforcementExecutor ?
    const reportProperties = this.getReportProperties(report, this.savedReportProperties, includeOnlyChanges, true)

    if ((includeOnlyChanges && !force) && Object.keys(reportProperties).length === 0) {
      return Promise.resolve(false)
    }

    const bodyFormData = new FormData()
    if (additionalProperties) {
      Object.assign(reportProperties, additionalProperties)
    }
    bodyFormData.set('properties', JSON.stringify(reportProperties))
    bodyFormData.set('changeTime', report.changeTime)

    let url
    if (report.reports && report.reports[0].id) { // сводный отчёт
      url = '/service/delta/report/multiple/edit'
      bodyFormData.set('reportId', report.reports[0].id)
    } else { // обычный отчёт
      url = '/service/delta/report/edit'
      bodyFormData.set('reportId', report.id)
    }

    if (checking) {
      url += '?checkState=false'
    }

    return axiosPost({
      url: url,
      data: bodyFormData,
      config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
      onSuccess: (response) => {
        this.savedReportPropertiesString = JSON.stringify(this.getReportProperties(report))

        target.$q.notify({
          type: 'positive',
          message: 'Отчёт сохранен',
          progress: true,
          timeout: 3000
        })

        if (response.data.message) {
          target.$q.notify({
            type: 'warning',
            message: response.data.message,
            progress: true,
            timeout: 8000
          })
        }

        return true
      },
      onFailure: (response) => {
        target.$q.notify({
          type: 'negative',
          message: response.message,
          progress: true,
          timeout: 8000
        })

        throw new Error(response.message)
      }
    })
  }

  // Файлы (фото и вложения) =======================================

  getFilesGroupsContainer (containerKey) {
    return this.report.filesGroupsContainers[containerKey]
  }

  hasFilesToUpload (containerKey) {
    const filesGroupsContainer = this.getFilesGroupsContainer(containerKey)
    return filesGroupsContainer && filesGroupsContainer.hasFilesToUpload() || false
  }

  uploadFiles (containerKey, target) {
    const filesGroupsContainer = this.getFilesGroupsContainer(containerKey)
    if (filesGroupsContainer) {
      return filesGroupsContainer.uploadFiles({
        uploadConfig: this.report && this.report.photoUploadConfig || null,
        notify: true,
        progress: true,
        target
      })
    }
    return Promise.resolve()
  }

  // Материалы =======================================

  // @deprecated
  hasMaterialsChanges () {
    if (this.report.reports) {
      for (const report of this.report.reports) {
        if (report._materialsGroups.hasChanges()) {
          return true
        }
      }
    } else {
      return this.report._materialsGroups.hasChanges()
    }
    return false
  }
}

class ReportError extends Error {
  constructor (message, result) {
    super(message)
    this.result = result || null
  }
}
